import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { 
  Home, Search, Trophy, User, LogOut, Shield, Settings,
  Navigation, AlertTriangle, Users, MapPinned, Siren,
  HeartHandshake, Sparkles, Heart, Bell, Crown, Cat
} from "lucide-react";

export const Layout = ({ children }) => {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const isAdmin = user?.email === "contact@rateshare.fr" || user?.is_admin;

  const navItems = [
    { path: "/dashboard", label: "Accueil", icon: Home },
    { path: "/crush", label: "Crush", icon: Heart, highlight: "pink" },
    { path: "/disparitions", label: "Disparitions", icon: Cat, highlight: "red" },
    { path: "/gps", label: "GPS", icon: MapPinned },
    { path: "/safecheck", label: "Safe Check", icon: Navigation },
    { path: "/urgences", label: "Urgences", icon: Siren, highlight: "red" },
    { path: "/bonnes-actions", label: "Entraide", icon: HeartHandshake, highlight: "green" },
    { path: "/cercles", label: "Cercles", icon: Users },
    { path: "/alertes", label: "Alertes", icon: AlertTriangle },
    { path: "/leaderboard", label: "Classement", icon: Trophy },
  ];

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  const getInitials = (name) => {
    return name
      ?.split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?";
  };

  const highlightColors = {
    pink: "text-pink-500 bg-pink-500/10",
    red: "text-red-500 bg-red-500/10",
    green: "text-green-500 bg-green-500/10"
  };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Ambient Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-pink-500/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/5 rounded-full blur-[150px]" />
      </div>

      {/* Header */}
      <header className="sticky top-0 z-50 bg-black/60 backdrop-blur-2xl border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2">
            <img src="/logo-header.png" alt="Logo" className="w-10 h-10" />
            <span className="text-xl font-bold hidden sm:block">RATE<span className="text-amber-400">⭐</span>SHARE</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.slice(0, 6).map((item) => {
              const isActive = location.pathname === item.path;
              const highlightClass = item.highlight ? highlightColors[item.highlight] : "";
              
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                    isActive 
                      ? 'bg-white/10 text-white' 
                      : `text-zinc-400 hover:text-white hover:bg-white/5 ${highlightClass}`
                  }`}
                >
                  <item.icon size={18} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <Link to="/notifications" className="relative p-2 rounded-xl hover:bg-white/5 transition-colors">
              <Bell size={20} className="text-zinc-400" />
            </Link>

            {/* Premium Badge */}
            {user?.is_premium && (
              <div className="hidden sm:flex items-center gap-1 px-3 py-1 rounded-full bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-pink-500/30">
                <Crown size={14} className="text-pink-400" />
                <span className="text-xs font-medium text-pink-400">Premium</span>
              </div>
            )}

            {/* User Menu */}
            <div className="relative group">
              <button className="flex items-center gap-2 p-2 rounded-xl hover:bg-white/5 transition-colors">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center text-sm font-bold">
                  {getInitials(user?.full_name)}
                </div>
              </button>

              {/* Dropdown */}
              <div className="absolute right-0 top-full mt-2 w-56 py-2 bg-zinc-900 border border-white/10 rounded-2xl shadow-xl opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all">
                <div className="px-4 py-2 border-b border-white/10">
                  <p className="font-medium">{user?.full_name}</p>
                  <p className="text-sm text-zinc-500">{user?.email}</p>
                </div>
                
                <Link to="/profile" className="flex items-center gap-3 px-4 py-2 hover:bg-white/5 transition-colors">
                  <User size={16} className="text-zinc-400" />
                  <span>Mon Profil</span>
                </Link>
                
                <Link to="/search" className="flex items-center gap-3 px-4 py-2 hover:bg-white/5 transition-colors">
                  <Search size={16} className="text-zinc-400" />
                  <span>Rechercher</span>
                </Link>

                {!user?.is_premium && (
                  <Link to="/premium" className="flex items-center gap-3 px-4 py-2 hover:bg-white/5 transition-colors text-pink-400">
                    <Crown size={16} />
                    <span>Passer Premium</span>
                  </Link>
                )}
                
                {isAdmin && (
                  <Link to="/admin-dashboard" className="flex items-center gap-3 px-4 py-2 hover:bg-white/5 transition-colors text-amber-400">
                    <Shield size={16} />
                    <span>Admin</span>
                  </Link>
                )}
                
                <div className="border-t border-white/10 mt-2 pt-2">
                  <button 
                    onClick={handleLogout}
                    className="flex items-center gap-3 px-4 py-2 w-full hover:bg-white/5 transition-colors text-red-400"
                  >
                    <LogOut size={16} />
                    <span>Déconnexion</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10">
        {children}
      </main>

      {/* Bottom Navigation (Mobile) */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 lg:hidden bg-black/80 backdrop-blur-2xl border-t border-white/5">
        <div className="flex items-center justify-around py-2 safe-area-bottom">
          {[
            { icon: Home, label: "Accueil", path: "/dashboard" },
            { icon: Heart, label: "Crush", path: "/crush" },
            { icon: MapPinned, label: "GPS", path: "/gps" },
            { icon: HeartHandshake, label: "Entraide", path: "/bonnes-actions" },
            { icon: User, label: "Profil", path: "/profile" }
          ].map((item) => {
            const isActive = location.pathname === item.path;
            return (
              <Link 
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center gap-1 px-3 py-1 rounded-xl transition-colors ${
                  isActive ? 'text-pink-500' : 'text-zinc-500'
                }`}
              >
                <item.icon size={20} />
                <span className="text-[10px]">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
